import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSystemSettings } from '../../hooks/useSystemSettings';

// ── Types ──────────────────────────────────────────────────────────────────────
interface ChecklistStep {
  id: string;
  title: string;
  description: string;
  icon: string;
  route: string;
  ctaLabel: string;
}

const STEPS: ChecklistStep[] = [
  {
    id: 'create_account',
    title: 'Criar conta financeira',
    description: 'Adicione sua conta bancária, poupança ou carteira digital.',
    icon: 'ri-bank-line',
    route: '/contas',
    ctaLabel: 'Criar conta',
  },
  {
    id: 'first_transaction',
    title: 'Registrar primeiro lançamento',
    description: 'Anote uma receita ou despesa para começar o controle.',
    icon: 'ri-exchange-dollar-line',
    route: '/lancamentos',
    ctaLabel: 'Adicionar lançamento',
  },
  {
    id: 'create_goal',
    title: 'Definir uma meta financeira',
    description: 'Estabeleça um objetivo — viagem, reserva, investimento.',
    icon: 'ri-focus-3-line',
    route: '/metas',
    ctaLabel: 'Criar meta',
  },
  {
    id: 'view_dashboard',
    title: 'Explorar o dashboard',
    description: 'Veja o resumo financeiro e os insights do mês.',
    icon: 'ri-dashboard-line',
    route: '/dashboard',
    ctaLabel: 'Ver dashboard',
  },
];

// ── localStorage helpers ───────────────────────────────────────────────────────
const STORAGE_KEY = 'panda_setup_checklist';

interface ChecklistState {
  completed: string[];
  dismissed: boolean;
}

const loadState = (userId: string): ChecklistState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { completed: [], dismissed: false };
    const all = JSON.parse(raw) as Record<string, ChecklistState>;
    return all[userId] ?? { completed: [], dismissed: false };
  } catch {
    return { completed: [], dismissed: false };
  }
};

const saveState = (userId: string, state: ChecklistState) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all = raw ? (JSON.parse(raw) as Record<string, ChecklistState>) : {};
    all[userId] = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
};

// ── Props ──────────────────────────────────────────────────────────────────────
interface Props {
  hasAccounts: boolean;
  hasTransactions: boolean;
  hasGoals: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────────
export const SetupChecklist = ({ hasAccounts, hasTransactions, hasGoals }: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSystemSettings();
  const appPrimary = settings.app_primary_color || '#10b981';

  const [state, setState] = useState<ChecklistState>({ completed: [], dismissed: false });
  const [collapsed, setCollapsed] = useState(false);

  // Load state on mount
  useEffect(() => {
    if (!user) return;
    const loaded = loadState(user.id);
    setState(loaded);
  }, [user]);

  // Auto-detect completed steps from real data
  useEffect(() => {
    if (!user) return;
    setState((prev) => {
      const completed = new Set(prev.completed);
      if (hasAccounts) completed.add('create_account');
      if (hasTransactions) completed.add('first_transaction');
      if (hasGoals) completed.add('create_goal');
      // Dashboard is "completed" after user has an account (they're using it)
      if (hasAccounts && hasTransactions) completed.add('view_dashboard');
      const next = { ...prev, completed: Array.from(completed) };
      saveState(user.id, next);
      return next;
    });
  }, [hasAccounts, hasTransactions, hasGoals, user]);

  const completedSet = new Set(state.completed);
  const completedCount = STEPS.filter((s) => completedSet.has(s.id)).length;
  const totalCount = STEPS.length;
  const pct = Math.round((completedCount / totalCount) * 100);
  const allDone = completedCount === totalCount;

  const handleDismiss = useCallback(() => {
    if (!user) return;
    const next = { ...state, dismissed: true };
    setState(next);
    saveState(user.id, next);
  }, [state, user]);

  const handleStepClick = (step: ChecklistStep) => {
    navigate(step.route);
  };

  // Don't show if dismissed or all steps done (after brief delay)
  if (!user || state.dismissed) return null;
  if (allDone) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer select-none"
        onClick={() => setCollapsed((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${appPrimary}18` }}
          >
            <i className="ri-rocket-line text-base" style={{ color: appPrimary }} />
          </div>
          <div>
            <p className="text-sm font-700 text-gray-900">
              Configure sua conta financeira
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {completedCount} de {totalCount} passos concluídos
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Progress ring */}
          <div className="flex items-center gap-2">
            <div className="relative w-9 h-9 shrink-0">
              <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="14" fill="none"
                  stroke={appPrimary} strokeWidth="3"
                  strokeDasharray={`${pct * 0.879} 87.9`}
                  strokeLinecap="round"
                />
              </svg>
              <span
                className="absolute inset-0 flex items-center justify-center text-[10px] font-700"
                style={{ color: appPrimary }}
              >
                {pct}%
              </span>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
            title="Fechar"
          >
            <i className="ri-close-line text-sm" />
          </button>
          <i className={`text-gray-400 text-sm transition-transform ${collapsed ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'}`} />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100 mx-5">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: appPrimary }}
        />
      </div>

      {/* Steps */}
      {!collapsed && (
        <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {STEPS.map((step, idx) => {
            const done = completedSet.has(step.id);
            return (
              <div
                key={step.id}
                onClick={() => !done && handleStepClick(step)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                  done
                    ? 'border-emerald-100 bg-emerald-50/40 opacity-60'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50 cursor-pointer'
                }`}
              >
                {/* Step number / check */}
                <div
                  className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
                    done ? 'bg-emerald-500' : 'bg-gray-100'
                  }`}
                >
                  {done ? (
                    <i className="ri-check-line text-white text-sm" />
                  ) : (
                    <span className="text-xs font-700 text-gray-500">{idx + 1}</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-600 leading-tight ${done ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                    {step.title}
                  </p>
                  {!done && (
                    <p className="text-[11px] text-gray-400 mt-0.5 leading-snug truncate">
                      {step.description}
                    </p>
                  )}
                </div>

                {/* Arrow */}
                {!done && (
                  <i className="ri-arrow-right-s-line text-gray-400 text-base shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
